import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { isSilentRecoveryOutcome } from '../utils/authExpiryGuard';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import MaritacaLogo from '../static/images/maritaca_logo_branca.png';
import { API_BASE } from '../shared/Constants';

const matchesEmailVerificationWarning = (text?: string | null) => {
  if (!text) return false;
  const normalized = text.toLowerCase().replace(/-/g, '');
  return /confirm|verif/.test(normalized) && /e?mails?/.test(normalized);
};

const USE_EXAMPLES = [
  {
    label: 'Investimentos',
    question: 'Poupança, imóvel ou Tesouro: o que rende mais?',
    summary: 'O Tesouro Selic foi o grande vencedor: +129,6% em 10 anos.',
    source: 'Fonte: Banco Central, Tesouro Direto e FipeZAP.',
  },
  {
    label: 'Moradia',
    question: 'Qual bairro do Recife tem boas escolas e é mais barato pra morar?',
    summary: 'Em Recife, Boa Viagem tem mais escolas, mas é o mais caro — o Casa Amarela oferece quase tanto por bem menos.',
    source: 'Fonte: Overture Maps (escolas), Censo 2022/IBGE (renda) e FipeZap.',
  },
  {
    label: 'Carreira',
    question: 'Quais profissões mais pagam em Porto Alegre exigindo só o ensino médio?',
    summary: 'Com ensino médio, dá pra ganhar até R$ 10,5 mil em Porto Alegre — policiais e técnicos lideram.',
    source: 'Fonte: RAIS 2024 (Ministério do Trabalho).',
  },
  {
    label: 'Negócios',
    question: 'Quanto custa abrir uma lanchonete em Copacabana?',
    summary: 'Abrir uma lanchonete em Copacabana custa R$ 70–160 mil — reforma e equipamentos pesam mais.',
    source: 'Fonte: FipeZap, Receita Federal e portais imobiliários.',
  },
  {
    label: 'Indústria',
    question: 'Qual estado tem a energia mais barata entre os mais industrializados?',
    summary: 'O Paraná tem a energia mais barata entre os estados industriais — só SP tem indústria maior, mas paga bem mais caro.',
    source: 'Fonte: ANEEL (tarifa), RAIS 2024 (empregos) e Comex 2025 (exportações).',
  },
] as const;

function GoogleMark() {
  return (
    <GoogleSvg viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M16.51 8H8.98v3h4.3c-.18 1-.74 1.48-1.6 2.04v2.01h2.6a7.8 7.8 0 0 0 2.38-5.88c0-.57-.05-.66-.15-1.18z" />
      <path fill="#34A853" d="M8.98 17c2.16 0 3.97-.72 5.3-1.94l-2.6-2.04a4.8 4.8 0 0 1-2.7.75 4.8 4.8 0 0 1-4.52-3.36H1.83v2.07A8 8 0 0 0 8.98 17z" />
      <path fill="#FBBC05" d="M4.46 10.41A4.8 4.8 0 0 1 4.21 9c0-.49.09-.97.25-1.41V5.52H1.83a8 8 0 0 0 0 7.16l2.63-2.27z" />
      <path fill="#EA4335" d="M8.98 3.58c1.32 0 2.5.45 3.44 1.35l2.54-2.54A8 8 0 0 0 8.98 0a8 8 0 0 0-7.15 4.42l2.63 2.27c.63-1.89 2.39-3.11 4.52-3.11z" />
    </GoogleSvg>
  );
}

function Login() {
  const { loginWithRedirect, isAuthenticated, isLoading } = useAuth0();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [showResendButton, setShowResendButton] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [activeExample, setActiveExample] = useState(0);

  const urlError = new URLSearchParams(window.location.search).get('error');
  const urlErrorDescription = new URLSearchParams(window.location.search).get('error_description');
  const hasAuthErrorParams = Boolean(urlError || urlErrorDescription);
  const isEmailVerificationAlert = matchesEmailVerificationWarning(alertMessage);

  useEffect(() => {
    if (!urlError) return;
    if (urlErrorDescription === 'account_linked') {
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      url.searchParams.delete('error_description');
      window.history.replaceState({}, document.title, url.toString());
      // O deny do account_linked NÃO deixa sessão SSO no Auth0; sem a connection
      // original o retry cai numa segunda tela de login. Repetindo a connection,
      // o retry re-federa no IdP (que tem sessão) e completa sem interação.
      const lastConnection = sessionStorage.getItem('auth0_last_connection') || undefined;
      const lastHint = sessionStorage.getItem('auth0_last_login_hint') || undefined;
      loginWithRedirect({
        authorizationParams: {
          scope: 'openid profile email offline_access',
          redirect_uri: window.location.origin + '/auth',
          connection: lastConnection,
          login_hint: lastHint,
        },
        appState: { returnTo: '/' },
      });
      return;
    }

    if (isSilentRecoveryOutcome(urlError)) {
      // Outcome of AuthExpiryGuard's prompt=none recovery: the Auth0 session
      // is gone too, so the user really has to sign in again. That is the
      // normal login screen, not an error to alarm them with.
      const url = new URL(window.location.href);
      url.searchParams.delete('error');
      url.searchParams.delete('error_description');
      url.searchParams.delete('state');
      window.history.replaceState({}, document.title, url.toString());
      setLoading(false);
      setIsSignupMode(false);
      return;
    }

    setAlertMessage(urlErrorDescription || 'Erro de autenticação');
    setShowResendButton(matchesEmailVerificationWarning(urlErrorDescription));
    const url = new URL(window.location.href);
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');
    window.history.replaceState({}, document.title, url.toString());
    setLoading(false);
    setIsSignupMode(false);
  }, [urlError, urlErrorDescription, loginWithRedirect]);

  useEffect(() => {
    if (hasAuthErrorParams || isEmailVerificationAlert) return;
    if (!isLoading && isAuthenticated) navigate('/', { replace: true });
  }, [isLoading, isAuthenticated, navigate, isEmailVerificationAlert, hasAuthErrorParams]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setActiveExample((activeExample + 1) % USE_EXAMPLES.length);
    }, 5500);
    return () => window.clearTimeout(timer);
  }, [activeExample]);

  const handleLogin = async () => {
    setLoading(true);
    setAlertMessage(null);
    setIsSignupMode(false);
    try {
      sessionStorage.setItem('auth0_last_connection', 'Username-Password-Authentication');
      if (email) sessionStorage.setItem('auth0_last_login_hint', email);
      await loginWithRedirect({
        authorizationParams: {
          connection: 'Username-Password-Authentication',
          scope: 'openid profile email offline_access',
          screen_hint: 'login',
          login_hint: email || undefined,
          redirect_uri: window.location.origin + '/auth',
          prompt: 'login',
        },
        appState: { returnTo: '/', mode: 'login' },
      });
    } catch (error) {
      console.error('Erro ao iniciar login:', error);
      setAlertMessage('Erro ao fazer login. Tente novamente.');
      setLoading(false);
    }
  };

  const handleSocialLogin = async () => {
    setAlertMessage(null);
    try {
      sessionStorage.setItem('auth0_last_connection', 'google-oauth2');
      sessionStorage.removeItem('auth0_last_login_hint');
      await loginWithRedirect({
        authorizationParams: {
          connection: 'google-oauth2',
          scope: 'openid profile email offline_access',
          redirect_uri: window.location.origin + '/auth',
          prompt: 'login',
        },
        appState: { returnTo: '/' },
      });
    } catch (error) {
      console.error('Erro no login social:', error);
      setAlertMessage('Erro ao fazer login social');
    }
  };

  const goToSignupDirect = () => {
    setLoading(true);
    setAlertMessage(null);
    setIsSignupMode(true);
    const domain = process.env.REACT_APP_AUTH_DOMAIN;
    const clientId = process.env.REACT_APP_AUTH_CLIENT_ID;
    const redirectUri = encodeURIComponent(window.location.origin);
    const scope = encodeURIComponent('openid profile email offline_access');
    window.location.href =
      `https://${domain}/authorize?response_type=code&client_id=${clientId}` +
      `&redirect_uri=${redirectUri}&scope=${scope}&screen_hint=signup&prompt=login&initial_screen=signUp` +
      (email ? `&login_hint=${encodeURIComponent(email)}` : '');
  };

  const handleResendVerification = async () => {
    if (!email) {
      setAlertMessage('Digite seu email acima para receber um novo link.');
      return;
    }
    setResendLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      if (response.ok) {
        setAlertMessage('Email de verificação reenviado. Confira sua caixa de entrada.');
        setShowResendButton(false);
      } else {
        const errorData = await response.json();
        setAlertMessage(errorData.detail || 'Não foi possível reenviar o email de verificação.');
      }
    } catch {
      setAlertMessage('Não foi possível reenviar o email. Tente novamente.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Page>
      <StoryPanel>
        <Rays data-testid="login-hero-background" src="/login-brasil.svg" alt="" aria-hidden="true" />
        <Vignette aria-hidden="true" />
        <StoryHeader>
          <Brand href="https://www.maritaca.ai" aria-label="Maritaca AI">
            <BrandLogo src={MaritacaLogo} alt="" />
            <BrandName>maritaca</BrandName>
          </Brand>
          <CountryTag>Feita no Brasil</CountryTag>
        </StoryHeader>

        <StoryContent>
          <Kicker>NOSSA LÍNGUA. NOSSA GENTE. NOSSA IA.</Kicker>
          <StoryTitle>Inteligência brasileira.<br /><em>Um mundo de possibilidades.</em></StoryTitle>
          <StoryText>
            Do tamanho das suas ideias. Com a nossa forma de falar, criar e ver o mundo.
            Conheça o Sabiá, a inteligência artificial da Maritaca.
          </StoryText>
          <Sample aria-label="Exemplo de conversa">
            <SampleLabel>{USE_EXAMPLES[activeExample].label}</SampleLabel>
            <SampleQuestion key={`question-${activeExample}`}>{USE_EXAMPLES[activeExample].question}</SampleQuestion>
            <EditorialResponse key={`answer-${activeExample}`} aria-label="Resposta do Sabiá">
              <ResponseByline><BirdMark aria-hidden="true">M</BirdMark><span>Sabiá · consultou bases oficiais</span></ResponseByline>
              <ResponseContent>
                <ResponseLead>{USE_EXAMPLES[activeExample].summary}</ResponseLead>

                {(activeExample === 0 || activeExample === 2) && (
                  <RankChart aria-label="Comparação dos resultados">
                    {(activeExample === 0
                      ? [['Tesouro Selic', '100%', '+129,6%'], ['Poupança', '44%', '+57,0%'], ['Imóvel Campinas', '35%', '+45,3%']]
                      : [['Policiais e guardas', '100%', 'R$ 10,5 mil'], ['Técnicos químicos', '79%', 'R$ 8,3 mil'], ['Téc. mecânica', '70%', 'R$ 7,4 mil']]
                    ).map(([name, width, value], index) => (
                      <RankRow key={name} $featured={index === 0}>
                        <span>{name}</span><i><b style={{ width }} /></i><strong>{value}</strong>
                      </RankRow>
                    ))}
                  </RankChart>
                )}

                {(activeExample === 1 || activeExample === 4) && (
                  <DotPlot aria-label="Comparação dos locais">
                    <DotAxis><span>{activeExample === 1 ? 'mais barato' : 'energia barata'}</span><span>{activeExample === 1 ? 'mais caro' : 'energia cara'}</span></DotAxis>
                    <Dot $x="24%" $y="52%" $featured><i />{activeExample === 1 ? 'Casa Amarela' : 'PR'}</Dot>
                    <Dot $x="72%" $y="24%"><i />{activeExample === 1 ? 'Boa Viagem' : 'SP'}</Dot>
                    <Dot $x="46%" $y="70%"><i />{activeExample === 1 ? 'Várzea' : 'SC'}</Dot>
                  </DotPlot>
                )}

                {activeExample === 3 && (
                  <CostVisual aria-label="Distribuição do investimento">
                    <CostRing><strong>R$ 70–160</strong><span>mil</span></CostRing>
                    <CostLegend><span><i />Reforma · 35%</span><span><i />Equipamentos · 27%</span><span><i />Capital de giro · 22%</span></CostLegend>
                  </CostVisual>
                )}

                <ResponseSource>{USE_EXAMPLES[activeExample].source}</ResponseSource>
              </ResponseContent>
            </EditorialResponse>
            <ExampleControls aria-label="Escolher exemplo de uso">
              <ExampleArrow
                type="button"
                aria-label="Exemplo anterior"
                onClick={() => setActiveExample((activeExample - 1 + USE_EXAMPLES.length) % USE_EXAMPLES.length)}
              >←</ExampleArrow>
              <ExampleDots>
                {USE_EXAMPLES.map((example, index) => (
                  <ExampleButton
                    key={example.label}
                    type="button"
                    aria-label={`Ver exemplo: ${example.label}`}
                    aria-pressed={activeExample === index}
                    $active={activeExample === index}
                    onClick={() => setActiveExample(index)}
                  />
                ))}
              </ExampleDots>
              <ExampleArrow
                type="button"
                aria-label="Próximo exemplo"
                onClick={() => setActiveExample((activeExample + 1) % USE_EXAMPLES.length)}
              >→</ExampleArrow>
            </ExampleControls>
          </Sample>
        </StoryContent>

      </StoryPanel>

      <LoginPanel>
        <MobileBrand>
          <MobileBrandLogo src={MaritacaLogo} alt="" />
          <BrandName>maritaca</BrandName>
        </MobileBrand>
        <LoginCard>
          <LoginEyebrow>Sua conta Maritaca</LoginEyebrow>
          <LoginTitle>Continue no Chat Maritaca</LoginTitle>
          <LoginSubtitle>Entre para retomar suas conversas e projetos com o Sabiá.</LoginSubtitle>

          {alertMessage && (
            <Alert role="alert">
              <AlertText>{alertMessage}</AlertText>
              {showResendButton && (
                <ResendButton type="button" onClick={handleResendVerification} disabled={resendLoading}>
                  {resendLoading ? 'Reenviando…' : 'Reenviar email de verificação'}
                </ResendButton>
              )}
            </Alert>
          )}

          <LoginForm onSubmit={(event) => { event.preventDefault(); handleLogin(); }}>
            <FormLabel htmlFor="login-email">Seu email</FormLabel>
            <FormInput
              id="login-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="voce@empresa.com.br"
              autoComplete="email"
              required
              autoFocus
            />
            <SubmitButton type="submit" disabled={loading}>
              {loading && !isSignupMode ? 'Abrindo…' : 'Continuar com email'}
              <Arrow aria-hidden="true">→</Arrow>
            </SubmitButton>
            <Divider aria-hidden="true"><span>ou</span></Divider>
            <GoogleButton type="button" onClick={handleSocialLogin} disabled={loading}>
              <GoogleMark /> Continuar com Google
            </GoogleButton>
          </LoginForm>

          <SignupLine>
            Primeira vez por aqui?{' '}
            <SignupButton type="button" onClick={goToSignupDirect} disabled={loading}>
              {loading && isSignupMode ? 'Abrindo…' : 'Crie sua conta'}
            </SignupButton>
          </SignupLine>
          <Terms>
            Ao continuar, você concorda com os{' '}
            <a href="https://www.maritaca.ai/termos-e-condicoes" target="_blank" rel="noreferrer">Termos</a>
            {' '}e a{' '}
            <a href="https://www.maritaca.ai/politica-de-privacidade" target="_blank" rel="noreferrer">Privacidade</a>.
          </Terms>
        </LoginCard>
        <HelpLink href="mailto:suporte@maritaca.ai">Precisa de ajuda?</HelpLink>
      </LoginPanel>
    </Page>
  );
}

export default Login;

const ink = '#15372f';
const green = '#1a3c34';
const paper = '#ffffff';
const orange = '#ff7f40';

const Page = styled.main`
  min-height: 100dvh;
  display: grid;
  grid-template-columns: minmax(520px, 1fr) minmax(460px, .9fr);
  background: ${green};
  color: ${ink};
  font-family: var(--font-primary);
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const StoryPanel = styled.section`
  min-height: 100dvh;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: clamp(28px, 3.8vw, 54px) clamp(34px, 4.5vw, 68px);
  background: ${green};
  color: #fff;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  isolation: isolate;
  @media (max-width: 860px) { display: none; }
`;

const StoryHeader = styled.header`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const Brand = styled.a`
  display: inline-flex; align-items: center; gap: 11px; color: #fff; text-decoration: none;
`;
const BrandLogo = styled.img`width: 38px; height: 38px; object-fit: contain;`;
const BrandName = styled.span`font-size: 22px; font-weight: 700; letter-spacing: -0.04em;`;
const CountryTag = styled.span`
  font-family: var(--font-secondary); font-size: 11px; font-weight: 700; letter-spacing: .12em;
  text-transform: uppercase; border: 1px solid rgba(255,255,255,.65); border-radius: 999px; padding: 7px 12px;
`;
const Rays = styled.img`
  position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover;
  opacity: 1; pointer-events: none; z-index: 0; user-select: none;
`;
const Vignette = styled.div`
  position: absolute; inset: 0; z-index: 1; pointer-events: none;
  background: linear-gradient(90deg, rgba(8,43,35,.35), transparent);
`;
const StoryContent = styled.div`
  position: relative; z-index: 2; width: min(590px, 100%); margin: auto 0; padding: 48px 0;
`;
const Kicker = styled.p`
  margin: 0 0 17px; font-family: var(--font-secondary); font-size: 10.5px; font-weight: 700;
  letter-spacing: .16em;
`;
const StoryTitle = styled.h1`
  max-width: 530px; margin: 0; font-size: clamp(42px, 4.15vw, 60px); font-weight: 600;
  line-height: 1.04; letter-spacing: -.045em;
  em { color: #f4d66f; font-style: normal; font-weight: 400; }
`;
const StoryText = styled.p`
  max-width: 530px; margin: 21px 0 0; font-family: var(--font-secondary); font-size: clamp(14px, 1.2vw, 17px);
  line-height: 1.55; color: rgba(255,255,255,.74);
`;
const Sample = styled.div`
  width: min(520px, 96%); margin-top: 30px; font-family: var(--font-secondary); font-size: 13px;
`;
const SampleLabel = styled.div`
  margin: 0 0 8px; color: rgba(255,255,255,.65); font-size: 10px; font-weight: 800;
  letter-spacing: .13em; text-transform: uppercase;
`;
const SampleQuestion = styled.div`
  width: fit-content; max-width: 92%; margin: 0 0 10px auto; padding: 10px 14px; background: #fff; color: ${ink};
  border-radius: 18px 18px 3px 18px;
  animation: example-in .35s ease both;
  @keyframes example-in { from { opacity: 0; transform: translateY(5px); } }
`;
const EditorialResponse = styled.div`
  width: 100%; padding: 15px 17px 17px; color: ${ink};
  border: 1px solid rgba(255,255,255,.7); border-radius: 3px 16px 16px 16px;
  background: #fff; box-shadow: 0 15px 35px rgba(5,25,19,.2);
  animation: example-in .35s .08s ease both;
`;
const BirdMark = styled.span`
  flex: 0 0 22px; height: 22px; display: grid; place-items: center; border-radius: 50%;
  background: ${green}; color: #fff; font-size: 9px; font-weight: 800;
`;
const ResponseByline = styled.div`
  display: flex; align-items: center; gap: 7px; margin-bottom: 12px;
  span { color: rgba(21,55,47,.62); font-size: 9.5px; letter-spacing: .03em; }
`;
const ResponseContent = styled.div`padding-left: 29px;`;
const ResponseLead = styled.p`
  max-width: 450px; margin: 0; color: ${ink}; font-family: var(--font-primary); font-size: 15px; line-height: 1.35;
  letter-spacing: -.01em;
`;
const RankChart = styled.div`display: grid; gap: 7px; margin-top: 12px;`;
const RankRow = styled.div<{ $featured: boolean }>`
  display: grid; grid-template-columns: 118px 1fr 70px; align-items: center; gap: 9px;
  span { overflow: hidden; color: rgba(21,55,47,${(props) => props.$featured ? '.95' : '.68'}); font-size: 9.5px; white-space: nowrap; text-overflow: ellipsis; }
  > i { height: 7px; overflow: hidden; border-radius: 99px; background: #e6ebe7; }
  > i b { display: block; height: 100%; border-radius: inherit; background: ${(props) => props.$featured ? '#63bd48' : '#a9d79a'}; }
  strong { color: ${(props) => props.$featured ? '#3d8e35' : 'rgba(21,55,47,.68)'}; font-size: 9.5px; text-align: right; }
`;
const DotPlot = styled.div`
  position: relative; height: 70px; margin-top: 11px; border-bottom: 1px solid rgba(21,55,47,.2);
`;
const DotAxis = styled.div`
  position: absolute; inset: auto 0 -13px; display: flex; justify-content: space-between;
  color: rgba(21,55,47,.48); font-size: 8px;
`;
const Dot = styled.span<{ $x: string; $y: string; $featured?: boolean }>`
  position: absolute; left: ${(props) => props.$x}; top: ${(props) => props.$y}; display: flex; align-items: center; gap: 4px;
  color: ${(props) => props.$featured ? ink : 'rgba(21,55,47,.65)'}; font-size: 8.5px; transform: translate(-50%, -50%);
  i { width: ${(props) => props.$featured ? '9px' : '6px'}; height: ${(props) => props.$featured ? '9px' : '6px'}; border-radius: 50%; background: ${(props) => props.$featured ? '#63bd48' : orange}; box-shadow: 0 0 0 3px rgba(21,55,47,.06); }
`;
const CostVisual = styled.div`display: flex; align-items: center; gap: 26px; margin-top: 11px;`;
const CostRing = styled.div`
  width: 74px; height: 74px; display: grid; place-content: center; flex: 0 0 74px; text-align: center; border-radius: 50%;
  background: radial-gradient(circle, #fff 57%, transparent 59%), conic-gradient(#63bd48 0 35%, ${orange} 35% 62%, #f3c56f 62% 84%, #dfe5df 84%);
  strong { font-size: 9.5px; } span { color: rgba(21,55,47,.6); font-size: 8px; }
`;
const CostLegend = styled.div`
  display: grid; gap: 7px; color: rgba(21,55,47,.74); font-size: 9px;
  span { display: flex; align-items: center; gap: 6px; } i { width: 6px; height: 6px; border-radius: 50%; background: #63bd48; }
  span:nth-child(2) i { background: ${orange}; } span:nth-child(3) i { background: #f3c56f; }
`;
const ResponseSource = styled.p`
  margin: 12px 0 0; color: rgba(21,55,47,.52); font-size: 9px; line-height: 1.4;
`;
const ExampleControls = styled.div`
  display: flex; justify-content: flex-end; align-items: center; gap: 10px; margin-top: 10px;
`;
const ExampleDots = styled.span`display: flex; align-items: center; gap: 6px;`;
const ExampleArrow = styled.button`
  width: 29px; height: 29px; padding: 0; display: grid; place-items: center;
  border: 1px solid rgba(255,255,255,.3); border-radius: 50%; background: rgba(26,60,52,.25);
  color: #fff; font-family: var(--font-secondary); font-size: 14px; cursor: pointer;
  transition: background .15s, border-color .15s;
  &:hover { background: rgba(255,255,255,.12); border-color: rgba(255,255,255,.6); }
  &:focus-visible { outline: 2px solid #fff; outline-offset: 2px; }
`;
const ExampleButton = styled.button<{ $active: boolean }>`
  width: ${(props) => props.$active ? '24px' : '7px'}; height: 7px; padding: 0; border: 0;
  border-radius: 999px; background: ${(props) => props.$active ? '#7ad943' : 'rgba(255,255,255,.35)'};
  cursor: pointer; transition: width .2s, background .2s;
  &:focus-visible { outline: 2px solid #fff; outline-offset: 3px; }
`;
const LoginPanel = styled.section`
  min-height: 100dvh; padding: 38px clamp(28px, 5vw, 72px) 26px; display: flex; flex-direction: column;
  justify-content: center; position: relative; background: #f2f1f1;
  border-left: 1px solid rgba(26,60,52,.08);
  @media (max-width: 860px) { justify-content: flex-start; padding-top: 24px; background: ${green}; }
  @media (max-width: 380px) { padding: 16px 18px 14px; }
`;
const MobileBrand = styled.div`
  display: none; align-items: center; gap: 9px; margin-bottom: 34px; color: #fff;
  ${BrandLogo} { width: 32px; height: 32px; }
  ${BrandName} { font-size: 21px; }
  @media (max-width: 860px) { display: flex; }
  @media (max-height: 620px) { margin-bottom: 18px; }
`;
const MobileBrandLogo = styled(BrandLogo)`filter: none;`;
const LoginCard = styled.div`
  width: 100%; max-width: 410px; margin: auto;
  @media (max-width: 860px) {
    margin: auto; padding: 30px 26px; background: #f2f1f1; border-radius: 22px;
  }
  @media (max-width: 380px) { padding: 26px 20px; }
`;
const LoginEyebrow = styled.p`
  margin: 0 0 12px; color: ${orange}; font-family: var(--font-secondary); font-size: 10.5px; font-weight: 800;
  letter-spacing: .14em; text-transform: uppercase;
`;
const LoginTitle = styled.h2`
  margin: 0; max-width: 390px; font-size: clamp(29px, 2.65vw, 38px); line-height: 1.05; letter-spacing: -.04em; font-weight: 500;
`;
const LoginSubtitle = styled.p`
  margin: 12px 0 30px; max-width: 350px; color: #617069; font-family: var(--font-secondary); font-size: 13px; line-height: 1.5;
  @media (max-height: 620px) { margin-bottom: 18px; }
`;
const Alert = styled.div`
  margin: 0 0 18px; padding: 13px 14px; border-left: 3px solid ${orange}; background: #fff2e8;
  color: #73351f; font-family: var(--font-secondary); font-size: 13px; line-height: 1.4;
`;
const AlertText = styled.div``;
const ResendButton = styled.button`
  margin: 9px 0 0; padding: 0; border: 0; background: none; color: ${green}; font: inherit;
  font-weight: 700; text-decoration: underline; cursor: pointer;
`;
const LoginForm = styled.form`display: flex; flex-direction: column;`;
const FormLabel = styled.label`
  margin-bottom: 8px; font-family: var(--font-secondary); font-size: 12px; font-weight: 700;
`;
const FormInput = styled.input`
  width: 100%; height: 52px; padding: 0 19px; border: 1px solid #b8c0bb; border-radius: 999px;
  background: #fff; color: ${ink}; font-family: var(--font-secondary); font-size: 14px; outline: none;
  transition: border-color .15s, box-shadow .15s;
  &::placeholder { color: #969b94; }
  &:focus { border-color: ${green}; box-shadow: 0 0 0 3px rgba(33,77,62,.12); }
`;
const ButtonBase = styled.button`
  width: 100%; height: 52px; border-radius: 2px; font-family: var(--font-secondary); font-size: 14px;
  font-weight: 700; cursor: pointer; transition: transform .12s, background .12s;
  &:active:not(:disabled) { transform: translateY(1px); }
  &:disabled { opacity: .65; cursor: wait; }
`;
const SubmitButton = styled(ButtonBase)`
  display: flex; align-items: center; justify-content: center; position: relative; margin-top: 10px;
  border: 1px solid ${orange}; background: ${orange}; color: white; border-radius: 999px;
  &:hover:not(:disabled) { background: #ed653b; }
`;
const Arrow = styled.span`position: absolute; right: 17px; font-size: 19px; font-weight: 400;`;
const Divider = styled.div`
  display: flex; align-items: center; gap: 12px; margin: 15px 0; color: #8c958f;
  font-family: var(--font-secondary); font-size: 10px; text-transform: uppercase;
  &::before, &::after { content: ''; flex: 1; height: 1px; background: #d8dcd9; }
`;
const GoogleButton = styled(ButtonBase)`
  display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 0;
  border: 1px solid #ccd3ce; background: transparent; color: ${ink}; border-radius: 999px;
  &:hover:not(:disabled) { background: rgba(255,255,255,.55); }
`;
const GoogleSvg = styled.svg`width: 18px; height: 18px; flex: 0 0 18px;`;
const SignupLine = styled.p`
  margin: 26px 0 0; text-align: center; color: #637069; font-family: var(--font-secondary); font-size: 12.5px;
`;
const SignupButton = styled.button`
  padding: 0; border: 0; background: none; color: ${green}; font: inherit; font-weight: 800; cursor: pointer;
  text-decoration: underline; text-underline-offset: 3px;
`;
const Terms = styled.p`
  margin: 24px auto 0; max-width: 390px; text-align: center; color: #848981; font-family: var(--font-secondary);
  font-size: 10.5px; line-height: 1.55;
  a { color: inherit; text-underline-offset: 2px; }
`;
const HelpLink = styled.a`
  position: absolute; right: 28px; bottom: 22px; color: #68736d; font-family: var(--font-secondary);
  font-size: 11px; text-decoration: none; border-bottom: 1px solid #a9aea8;
  @media (max-width: 860px) { position: static; margin: 22px auto 0; color: rgba(255,255,255,.72); border-color: rgba(255,255,255,.35); }
  @media (max-height: 620px) { display: none; }
`;
